import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Heart, Mail, Lock, Phone, MessageSquare, Shield, Eye, EyeOff, Check, Crown, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Auth = () => {
  const { login, API } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Auth method: 'email' or 'phone'
  const [authMethod, setAuthMethod] = useState('email');
  
  // Password visibility toggle (Problem 3)
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  
  // Selected plan for registration (Problem 2)
  const [selectedPlan, setSelectedPlan] = useState('social');
  
  // OTP flow states (Problem 1)
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [registerOtpSent, setRegisterOtpSent] = useState(false);
  const [registerOtpCode, setRegisterOtpCode] = useState('');
  
  // 2FA flow states
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const [loginData, setLoginData] = useState({ email: '', phone: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', phone: '' });
  
  // GDPR Consent states
  const [gdprConsent, setGdprConsent] = useState({
    termsAccepted: false,
    privacyAccepted: false
  });

  // Check URL for pre-selected plan
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan && ['social', 'basic', 'premium', 'gold'].includes(plan)) {
      setSelectedPlan(plan);
    }
  }, [searchParams]);

  // Plan configurations
  const plans = [
    { id: 'social', name: 'Plan Social', price: '0', icon: '🤝', color: 'teal', description: 'Parte din comunitatea NutriHealth' },
    { id: 'basic', name: 'Basic', price: '29.99', icon: '🌱', color: 'emerald' },
    { id: 'premium', name: 'Premium', price: '59.99', icon: '⭐', color: 'purple', popular: true },
    { id: 'gold', name: 'Gold', price: '99.99', icon: '👑', color: 'amber', exclusive: true },
  ];

  // Email/Password Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = authMethod === 'email' 
        ? { email: loginData.email, password: loginData.password }
        : { phone: loginData.phone, password: loginData.password };
      
      const response = await axios.post(`${API}/auth/login`, payload);
      
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
        toast.info('Introdu codul din aplicația de autentificare');
        return;
      }
      
      login(response.data.token, response.data.user);
      toast.success('Bine ai revenit!');
      navigate('/dashboard');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || JSON.stringify(errorDetail)) 
        : (errorDetail || 'Eroare la autentificare');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Validate 2FA code
  const handleValidate2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/2fa/validate`, 
        { code: twoFactorCode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      
      login(response.data.token, response.data.user);
      toast.success('Autentificare reușită!');
      navigate('/dashboard');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || 'Cod 2FA invalid') 
        : (errorDetail || 'Cod 2FA invalid');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Request OTP for phone login (Problem 1)
  const handleRequestOTP = async () => {
    if (!loginData.phone) {
      toast.error('Introdu numărul de telefon');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/request-otp`, {
        phone: loginData.phone
      });
      
      setOtpSent(true);
      
      // Check if SMS was actually sent or mocked
      if (response.data.sms_sent) {
        toast.success('✅ Cod OTP trimis prin SMS!');
      } else {
        toast.success('Cod OTP generat!');
        // Show demo OTP if available
        if (response.data.demo_otp) {
          toast.info(`[Demo] Cod OTP: ${response.data.demo_otp}`, { duration: 10000 });
        }
        if (response.data.note) {
          toast.warning(response.data.note, { duration: 8000 });
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || 'Eroare la trimiterea OTP') 
        : (errorDetail || 'Eroare la trimiterea OTP');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP for phone login
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode) {
      toast.error('Introdu codul OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/verify-otp`, {
        phone: loginData.phone,
        otp: otpCode
      });
      
      login(response.data.token, response.data.user);
      
      if (response.data.is_new_user) {
        toast.success('Cont creat cu succes!');
        navigate('/profile');
      } else {
        toast.success('Bine ai revenit!');
        navigate('/dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || 'Cod OTP invalid') 
        : (errorDetail || 'Cod OTP invalid');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Request OTP for phone registration (Problem 1 - for registration)
  const handleRequestRegisterOTP = async () => {
    if (!registerData.phone) {
      toast.error('Introdu numărul de telefon');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/phone/request-otp`, {
        phone: registerData.phone
      });
      
      setRegisterOtpSent(true);
      
      // Check if SMS was actually sent or mocked
      if (response.data.sms_sent) {
        toast.success('✅ Cod OTP trimis prin SMS!');
      } else {
        toast.success('Cod OTP generat pentru verificare!');
        // Show demo OTP if available
        if (response.data.demo_otp) {
          toast.info(`[Demo] Cod OTP: ${response.data.demo_otp}`, { duration: 15000 });
        }
        if (response.data.note) {
          toast.warning(response.data.note, { duration: 8000 });
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || 'Eroare la trimiterea OTP') 
        : (errorDetail || 'Eroare la trimiterea OTP');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Register with selected plan (Problem 2)
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // GDPR Consent validation
    if (!gdprConsent.termsAccepted || !gdprConsent.privacyAccepted) {
      toast.error('Trebuie să accepți Termenii și Condițiile și Politica de Confidențialitate');
      return;
    }
    
    // Validate phone OTP if phone method
    if (authMethod === 'phone' && !registerOtpSent) {
      toast.error('Te rugăm să verifici numărul de telefon cu codul OTP');
      return;
    }
    
    if (authMethod === 'phone' && !registerOtpCode) {
      toast.error('Introdu codul OTP primit pe telefon');
      return;
    }
    
    setLoading(true);

    try {
      let response;
      
      if (authMethod === 'phone') {
        // Verify OTP first, then register
        response = await axios.post(`${API}/auth/phone/verify-otp`, {
          phone: registerData.phone,
          otp: registerOtpCode
        });
      } else {
        // Register with email
        response = await axios.post(`${API}/auth/register`, {
          email: registerData.email,
          password: registerData.password,
          phone: registerData.phone || null
        });
      }
      
      const userId = response.data.user?.id;
      const token = response.data.token;
      
      // Set user tier based on selected plan (Problem 2)
      if (selectedPlan !== 'social' && token) {
        try {
          await axios.put(
            `${API}/users/set-tier`,
            { tier: selectedPlan },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (tierError) {
          console.log('Tier will be set after payment');
        }
      }
      
      login(token, response.data.user);
      toast.success('Cont creat cu succes!');
      
      // Redirect based on selected plan
      if (selectedPlan !== 'social') {
        navigate('/subscription?plan=' + selectedPlan);
      } else {
        navigate('/profile');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? (errorDetail.msg || JSON.stringify(errorDetail)) 
        : (errorDetail || 'Eroare la înregistrare');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="border-none card-shadow">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-[#1A4D2E] rounded-full flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
              NutriHealth
            </CardTitle>
            <CardDescription>
              Platformă pentru o viață sănătoasă
            </CardDescription>
          </CardHeader>

          <CardContent>
            {requires2FA ? (
              <form onSubmit={handleValidate2FA} className="space-y-4">
                <div className="text-center mb-4">
                  <Shield className="w-12 h-12 text-[#1A4D2E] mx-auto mb-2" />
                  <p className="text-[#4a5568]">Autentificare în doi pași</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="2fa-code">Cod din aplicația de autentificare</Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-2xl tracking-widest"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1A4D2E] text-white rounded-full"
                >
                  {loading ? 'Se verifică...' : 'Verifică'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setRequires2FA(false); setTempToken(''); setTwoFactorCode(''); }}
                  className="w-full"
                >
                  Anulează
                </Button>
              </form>
            ) : (
              <Tabs defaultValue="login" className="space-y-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="login-tab">Autentificare</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Înregistrare</TabsTrigger>
                </TabsList>

                {/* LOGIN TAB */}
                <TabsContent value="login" className="space-y-4">
                  {/* Auth method selector */}
                  <div className="flex space-x-2 mb-4">
                    <Button
                      type="button"
                      variant={authMethod === 'email' ? 'default' : 'outline'}
                      onClick={() => { setAuthMethod('email'); setOtpSent(false); }}
                      className={`flex-1 ${authMethod === 'email' ? 'bg-[#1A4D2E]' : ''}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      type="button"
                      variant={authMethod === 'phone' ? 'default' : 'outline'}
                      onClick={() => { setAuthMethod('phone'); setOtpSent(false); }}
                      className={`flex-1 ${authMethod === 'phone' ? 'bg-[#1A4D2E]' : ''}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Telefon
                    </Button>
                  </div>

                  {authMethod === 'email' ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                          id="login-email"
                          type="email"
                          data-testid="login-email-input"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          placeholder="email@exemplu.com"
                          className="bg-[#F5F5F0]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="login-password">Parolă</Label>
                        <div className="relative">
                          <Input
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            data-testid="login-password-input"
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            placeholder="••••••••"
                            className="bg-[#F5F5F0] pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        data-testid="login-submit-button"
                        disabled={loading}
                        className="w-full bg-[#1A4D2E] text-white rounded-full"
                      >
                        {loading ? 'Se încarcă...' : 'Autentificare'}
                      </Button>
                    </form>
                  ) : (
                    /* Phone Login with OTP */
                    <div className="space-y-4">
                      {!otpSent ? (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="login-phone">Număr de telefon</Label>
                            <Input
                              id="login-phone"
                              type="tel"
                              value={loginData.phone}
                              onChange={(e) => setLoginData({ ...loginData, phone: e.target.value })}
                              placeholder="+40712345678"
                              className="bg-[#F5F5F0]"
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={handleRequestOTP}
                            disabled={loading}
                            className="w-full bg-[#1A4D2E] text-white rounded-full"
                          >
                            {loading ? 'Se trimite...' : 'Trimite cod OTP'}
                          </Button>
                        </>
                      ) : (
                        <form onSubmit={handleVerifyOTP} className="space-y-4">
                          <div className="text-center p-4 bg-green-50 rounded-lg mb-4">
                            <MessageSquare className="w-8 h-8 text-green-600 mx-auto mb-2" />
                            <p className="text-green-800 font-medium">Cod trimis la {loginData.phone}</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="otp-code">Cod OTP</Label>
                            <Input
                              id="otp-code"
                              type="text"
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value)}
                              placeholder="123456"
                              maxLength={6}
                              className="text-center text-2xl tracking-widest bg-[#F5F5F0]"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#1A4D2E] text-white rounded-full"
                          >
                            {loading ? 'Se verifică...' : 'Verifică și Autentifică'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => { setOtpSent(false); setOtpCode(''); }}
                            className="w-full"
                          >
                            Trimite alt cod
                          </Button>
                        </form>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-4 text-[#4a5568]">sau</span>
                    </div>
                  </div>
                  
                  {/* Google Login */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const redirectUrl = window.location.origin + '/dashboard';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full border-gray-200 rounded-full"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continuă cu Google
                  </Button>
                  
                  <div className="text-center mt-4">
                    <a href="/reset-password" className="text-sm text-[#1A4D2E] hover:underline">
                      Ai uitat parola?
                    </a>
                  </div>
                </TabsContent>

                {/* REGISTER TAB */}
                <TabsContent value="register" className="space-y-4">
                  {/* Plan Selection (Problem 2) */}
                  <div className="mb-4">
                    <Label className="text-base font-medium mb-3 block">Alege planul tău</Label>
                    
                    {/* Social Plan Message */}
                    <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">🤝</span>
                        <span className="font-medium text-teal-700">De ce Plan Social?</span>
                      </div>
                      <p className="text-sm text-teal-600">
                        Devii parte din comunitatea NutriHealth! Accesul la planul social îți oferă posibilitatea 
                        de a fi conectat cu alți membri și de a contribui la o viață sănătoasă împreună.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {plans.map((plan) => (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlan(plan.id)}
                          className={`p-3 rounded-xl border-2 transition-all text-left relative ${
                            selectedPlan === plan.id
                              ? plan.color === 'amber' 
                                ? 'border-amber-500 bg-amber-50'
                                : plan.color === 'purple'
                                  ? 'border-purple-500 bg-purple-50'
                                  : plan.color === 'emerald'
                                    ? 'border-emerald-500 bg-emerald-50'
                                    : plan.color === 'teal'
                                      ? 'border-teal-500 bg-teal-50'
                                      : 'border-gray-500 bg-gray-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          {selectedPlan === plan.id && (
                            <div className="absolute top-2 right-2">
                              <Check className={`w-4 h-4 ${
                                plan.color === 'amber' ? 'text-amber-600' :
                                plan.color === 'purple' ? 'text-purple-600' :
                                plan.color === 'emerald' ? 'text-emerald-600' : 
                                plan.color === 'teal' ? 'text-teal-600' : 'text-gray-600'
                              }`} />
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">{plan.icon}</span>
                            <span className="font-medium text-[#1A4D2E]">{plan.name}</span>
                          </div>
                          <div className="text-sm text-[#4a5568]">
                            {plan.price === '0' ? 'Gratuit' : `${plan.price} RON/lună`}
                          </div>
                          {plan.description && (
                            <div className="text-xs text-teal-600 mt-1">{plan.description}</div>
                          )}
                          {plan.popular && (
                            <Badge className="mt-1 bg-purple-500 text-white text-xs">Popular</Badge>
                          )}
                          {plan.exclusive && (
                            <Badge className="mt-1 bg-amber-500 text-white text-xs">Exclusiv</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Auth method selector */}
                  <div className="flex space-x-2 mb-4">
                    <Button
                      type="button"
                      variant={authMethod === 'email' ? 'default' : 'outline'}
                      onClick={() => { setAuthMethod('email'); setRegisterOtpSent(false); }}
                      className={`flex-1 ${authMethod === 'email' ? 'bg-[#1A4D2E]' : ''}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      type="button"
                      variant={authMethod === 'phone' ? 'default' : 'outline'}
                      onClick={() => { setAuthMethod('phone'); setRegisterOtpSent(false); }}
                      className={`flex-1 ${authMethod === 'phone' ? 'bg-[#1A4D2E]' : ''}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Telefon
                    </Button>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-4">
                    {authMethod === 'email' ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="register-email">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            data-testid="register-email-input"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            placeholder="email@exemplu.com"
                            className="bg-[#F5F5F0]"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="register-password">Parolă</Label>
                          <div className="relative">
                            <Input
                              id="register-password"
                              type={showRegisterPassword ? 'text' : 'password'}
                              data-testid="register-password-input"
                              value={registerData.password}
                              onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                              placeholder="Minim 6 caractere"
                              className="bg-[#F5F5F0] pr-10"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="register-phone">Telefon (opțional)</Label>
                          <Input
                            id="register-phone"
                            type="tel"
                            value={registerData.phone}
                            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                            placeholder="+40712345678"
                            className="bg-[#F5F5F0]"
                          />
                        </div>
                      </>
                    ) : (
                      /* Phone Registration with OTP (Problem 1) */
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="register-phone-main">Număr de telefon</Label>
                          <div className="flex gap-2">
                            <Input
                              id="register-phone-main"
                              type="tel"
                              value={registerData.phone}
                              onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                              placeholder="+40712345678"
                              className="bg-[#F5F5F0] flex-1"
                              required
                            />
                            <Button
                              type="button"
                              onClick={handleRequestRegisterOTP}
                              disabled={loading || !registerData.phone}
                              variant="outline"
                              className="whitespace-nowrap"
                            >
                              {registerOtpSent ? 'Retrimite' : 'Trimite OTP'}
                            </Button>
                          </div>
                        </div>

                        {registerOtpSent && (
                          <div className="space-y-2">
                            <div className="text-center p-3 bg-green-50 rounded-lg">
                              <p className="text-green-800 text-sm">✓ Cod trimis la {registerData.phone}</p>
                            </div>
                            <Label htmlFor="register-otp">Cod OTP</Label>
                            <Input
                              id="register-otp"
                              type="text"
                              value={registerOtpCode}
                              onChange={(e) => setRegisterOtpCode(e.target.value)}
                              placeholder="123456"
                              maxLength={6}
                              className="text-center text-xl tracking-widest bg-[#F5F5F0]"
                              required
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label htmlFor="register-password-phone">Parolă</Label>
                          <div className="relative">
                            <Input
                              id="register-password-phone"
                              type={showRegisterPassword ? 'text' : 'password'}
                              value={registerData.password}
                              onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                              placeholder="Minim 6 caractere"
                              className="bg-[#F5F5F0] pr-10"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Selected Plan Display */}
                    <div className={`p-3 rounded-lg border-2 ${
                      selectedPlan === 'gold' ? 'bg-amber-50 border-amber-300' :
                      selectedPlan === 'premium' ? 'bg-purple-50 border-purple-300' :
                      selectedPlan === 'basic' ? 'bg-emerald-50 border-emerald-300' :
                      selectedPlan === 'social' ? 'bg-teal-50 border-teal-300' :
                      'bg-gray-50 border-gray-300'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{plans.find(p => p.id === selectedPlan)?.icon}</span>
                          <span className="font-medium">{plans.find(p => p.id === selectedPlan)?.name}</span>
                        </div>
                        <span className="font-bold">
                          {selectedPlan === 'social' ? 'Gratuit' : `${plans.find(p => p.id === selectedPlan)?.price} RON/lună`}
                        </span>
                      </div>
                    </div>

                    {/* GDPR Consent Checkboxes */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 font-medium mb-2">Conform GDPR, ai nevoie să accepți:</p>
                      
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={gdprConsent.termsAccepted}
                          onChange={(e) => setGdprConsent({...gdprConsent, termsAccepted: e.target.checked})}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E]"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">
                          Accept <a href="/terms" target="_blank" className="text-[#1A4D2E] underline hover:text-[#143d24]">Termenii și Condițiile</a> de utilizare a platformei NutriHealth
                        </span>
                      </label>
                      
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={gdprConsent.privacyAccepted}
                          onChange={(e) => setGdprConsent({...gdprConsent, privacyAccepted: e.target.checked})}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1A4D2E] focus:ring-[#1A4D2E]"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">
                          Accept <a href="/privacy" target="_blank" className="text-[#1A4D2E] underline hover:text-[#143d24]">Politica de Confidențialitate</a> și procesarea datelor personale
                        </span>
                      </label>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Datele tale sunt protejate conform Regulamentului GDPR (UE) 2016/679.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || (authMethod === 'phone' && !registerOtpSent) || !gdprConsent.termsAccepted || !gdprConsent.privacyAccepted}
                      className={`w-full rounded-full text-white ${
                        selectedPlan === 'gold' 
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600' 
                          : selectedPlan === 'premium'
                            ? 'bg-purple-600 hover:bg-purple-700'
                            : selectedPlan === 'basic'
                              ? 'bg-emerald-600 hover:bg-emerald-700'
                              : selectedPlan === 'social'
                                ? 'bg-teal-600 hover:bg-teal-700'
                                : 'bg-[#1A4D2E] hover:bg-[#143d24]'
                      }`}
                    >
                      {loading ? 'Se creează contul...' : `Creează Cont ${selectedPlan !== 'social' ? plans.find(p => p.id === selectedPlan)?.name : 'Social'}`}
                    </Button>
                  </form>

                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-4 text-[#4a5568]">sau</span>
                    </div>
                  </div>
                  
                  {/* Google Register */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const redirectUrl = window.location.origin + '/dashboard';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full border-gray-200 rounded-full"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Înregistrează-te cu Google
                  </Button>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Auth;
